import React from 'react';
import ArrowDown from '@atlaskit/icon/core/arrow-down';
import ArrowUp from '@atlaskit/icon/core/arrow-up';
import {
	ORDER_BY_DIRECTION_ASC,
	ORDER_BY_DIRECTION_DESC,
	type OrderByDirectionValue,
} from '@atlaskit/jql-ast';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

type Props = {
	fieldDisplayText: string;
	fieldSortDirection?: OrderByDirectionValue;
};

export const SortDirectionIcon = ({ fieldDisplayText, fieldSortDirection }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<>
			<Box
				as="span"
				xcss={[fieldSortDirection === ORDER_BY_DIRECTION_ASC ? iconButtonStyles : displayNoneStyle]}
			>
				{}
				<ArrowUp
					label={formatMessage(messages.sortButtonLabelAsc, {
						field: fieldDisplayText,
					})}
				/>
			</Box>
			<Box
				as="span"
				xcss={[
					fieldSortDirection === ORDER_BY_DIRECTION_DESC ? iconButtonStyles : displayNoneStyle,
				]}
			>
				{}
				<ArrowDown
					label={formatMessage(messages.sortButtonLabelDesc, {
						field: fieldDisplayText,
					})}
				/>
			</Box>
			<Box
				as="span"
				xcss={[fieldSortDirection === undefined ? iconButtonStyles : displayNoneStyle]}
			>
				{}
				<ArrowDown
					label={formatMessage(messages.sortButtonWithDefaultAscDirectionLabel, {
						field: fieldDisplayText,
					})}
				/>
			</Box>
		</>
	);
};

/**
 * There is a bug with the new Atlaskit IconButton where occasionally the onClick handler is not
 * triggered, when wrapped by a tooltip - https://atlassian.slack.com/archives/CFJ9DU39U/p1718597982581429
 * Instead, using a legacy button but making it appear 32px wide so that it looks like the new icon
 * button. Any narrower and it becomes an a11y issue having such a small click target
 */
const iconButtonStyles = xcss({
	display: 'inline-block',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
});

/**
 * Used to hide the icon that is not currently mapped to the fieldSortDirection using CSS instead of
 * React.
 *
 * For example:
 * If the fieldSortDirection is ASC, the <ArrowDown> will receive this style and be hidden.
 *
 * using CSS to show the correct icon keeps both icons in the DOM whereas conditionally rendering
 * based on `fieldSortDirection` will remove the unrelated icon from the DOM completely, on click.

 * This removal from the DOM interferes with onClick management in popper:
 * jira/platform/packages/design-system/popup/src/use-close-manager.tsx
 *
 * In the above file, if an element has gone missing from the DOM, the onClose functionality will
 * return early and leave any open pickers still open. Instead I want any open pickers to close if the
 * user clicks on a button elsewhere on the page (such as clicking on this icon when it is in a button)
 */
const displayNoneStyle = xcss({
	display: 'none',
});
